import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, Clock, Users, MapPin, Download, CreditCard, 
  Check, FileText, ArrowRight, Loader2, AlertCircle, CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  food: 'Food & Catering',
  drinks: 'Bar & Beverages',
  entertainment: 'Entertainment',
  staffing: 'Staffing',
  logistics: 'Logistics',
  decor: 'Decor',
  photography: 'Photography',
  other: 'Other'
};

export default function QuoteLandingPage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  
  const wasCancelled = searchParams.get('cancelled') === 'true';

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  const fetchQuote = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/quotes/${quoteId}`);
      setQuote(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch quote:', err);
      setError('Quote not found or has expired');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptQuote = async () => {
    setAccepting(true);
    try {
      const response = await axios.post(`${API}/quotes/${quoteId}/accept`, {
        origin_url: window.location.origin
      });
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Failed to accept quote:', err);
      toast.error(err.response?.data?.detail || 'Failed to process payment');
      setAccepting(false);
    }
  };

  const handleDownloadPDF = () => {
    window.open(`${API}/quotes/${quoteId}/pdf`, '_blank');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (time) => {
    if (!time) return 'TBD';
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your quote...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Quote Not Found</h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/plan-event')}>
              Create a New Quote
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const event = quote?.event_details || {};
  const isAccepted = quote?.status === 'accepted';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full px-4 py-2 mb-4">
            <FileText className="h-4 w-4" />
            <span className="text-sm font-medium">Quote #{quoteId}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="quote-title">
            Your Event Quote
          </h1>
          <p className="text-muted-foreground">
            Prepared for <span className="font-semibold text-foreground">{quote?.customer_name}</span>
          </p>
        </div>

        {/* Cancelled Banner */}
        {wasCancelled && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
            <p className="text-amber-800 dark:text-amber-200">
              Payment was cancelled. You can try again when you're ready.
            </p>
          </div>
        )}

        {/* Accepted Banner */}
        {isAccepted && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
            <p className="text-green-800 dark:text-green-200">
              This quote has been accepted. Thank you for your booking!
            </p>
          </div>
        )}

        {/* Main Card */}
        <Card className="shadow-xl border-0" data-testid="quote-card">
          {/* Event Details Header */}
          <CardHeader className="bg-gradient-to-r from-indigo-500 to-purple-600 text-foreground rounded-t-lg">
            <CardTitle className="text-2xl">Event Details</CardTitle>
            <CardDescription className="text-foreground/80">
              {formatDate(event.date)}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Event Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 dark:bg-muted rounded-lg p-4">
                <div className="flex items-center gap-2 text-indigo-500 mb-1">
                  <Calendar className="h-4 w-4" />
                  <span className="text-xs font-medium uppercase">Date</span>
                </div>
                <p className="font-semibold">{event.date || 'TBD'}</p>
              </div>
              <div className="bg-slate-50 dark:bg-muted rounded-lg p-4">
                <div className="flex items-center gap-2 text-indigo-500 mb-1">
                  <Clock className="h-4 w-4" />
                  <span className="text-xs font-medium uppercase">Time</span>
                </div>
                <p className="font-semibold">{formatTime(event.time)}</p>
                <p className="text-xs text-muted-foreground">{event.duration_hours} hours</p>
              </div>
              <div className="bg-slate-50 dark:bg-muted rounded-lg p-4">
                <div className="flex items-center gap-2 text-indigo-500 mb-1">
                  <Users className="h-4 w-4" />
                  <span className="text-xs font-medium uppercase">Guests</span>
                </div>
                <p className="font-semibold">{event.guest_count} people</p>
              </div>
              <div className="bg-slate-50 dark:bg-muted rounded-lg p-4">
                <div className="flex items-center gap-2 text-indigo-500 mb-1">
                  <MapPin className="h-4 w-4" />
                  <span className="text-xs font-medium uppercase">Location</span>
                </div>
                <p className="font-semibold">ZIP {event.venue_zip_code}</p>
              </div>
            </div>

            {/* Services */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Services Included</h3>
              <div className="space-y-3">
                {quote?.items?.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-4 bg-slate-50 dark:bg-muted rounded-lg"
                    data-testid={`quote-item-${idx}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{item.name}</p>
                        <Badge variant="outline" className="text-xs">
                          {CATEGORY_LABELS[item.category] || item.category}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Qty: {item.quantity} • {item.pricing_model?.replace('_', ' ')}
                      </p>
                      {item.selected_options?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.selected_options.map((opt, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {opt.choice_name}
                              {opt.price > 0 && ` (+$${opt.price.toFixed(2)})`}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="font-semibold text-lg">${item.total_price?.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>${quote?.subtotal?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Service Fee (15%)</span>
                <span>${quote?.admin_fee?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold pt-2 border-t">
                <span>Total</span>
                <span className="text-indigo-500">${quote?.total?.toFixed(2)}</span>
              </div>
            </div>

            {/* Validity Notice */}
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 text-center">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                This quote is valid for <strong>7 days</strong> from {quote?.created_at?.split('T')[0]}. 
                Prices may vary based on final details.
              </p>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col sm:flex-row gap-3 bg-slate-50 dark:bg-muted p-6 rounded-b-lg">
            <Button 
              variant="outline" 
              className="w-full sm:w-auto"
              onClick={handleDownloadPDF}
              data-testid="download-pdf-btn"
            >
              <Download className="mr-2 h-4 w-4" /> Download PDF
            </Button>
            
            {!isAccepted ? (
              <Button 
                className="w-full sm:flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700" 
                size="lg"
                onClick={handleAcceptQuote}
                disabled={accepting}
                data-testid="accept-quote-btn"
              >
                {accepting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Accept & Pay ${quote?.total?.toFixed(2)}
                  </>
                )}
              </Button>
            ) : (
              <Button 
                className="w-full sm:flex-1" 
                size="lg"
                onClick={() => navigate('/dashboard')}
              >
                <Check className="mr-2 h-4 w-4" />
                Go to Dashboard
              </Button>
            )}
          </CardFooter>
        </Card>

        {/* Contact Info */}
        <div className="text-center mt-8 text-muted-foreground">
          <p className="text-sm">
            Questions about this quote? Contact us at{' '}
            <a href="mailto:hello@eventism.com" className="text-indigo-500 hover:underline">
              hello@eventism.com
            </a>
            {' '}or call <a href="tel:5551234567" className="text-indigo-500 hover:underline">(555) 123-4567</a>
          </p>
        </div>

        {/* Back to Planning */}
        <div className="text-center mt-6">
          <Button variant="ghost" onClick={() => navigate('/plan-event')}>
            <ArrowRight className="mr-2 h-4 w-4 rotate-180" />
            Plan Another Event
          </Button>
        </div>
      </div>
    </div>
  );
}
